import React from 'react';
import { usePageSeo } from '../Seo/PageSeo';
import './PrivacyPolicy.css';

const PRIVACY_LAST_UPDATED = '21 October 2025';

const PrivacyPolicy = () => {
  usePageSeo({
    title: 'Privacy Policy',
    description: 'How Occasia collects, uses, and protects personal data when you book venues or list your space.',
    path: '/privacy'
  });

  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-date">Last updated: {PRIVACY_LAST_UPDATED}</p>

        <section className="privacy-section">
          <p>
            This Privacy Policy describes how Occasia (&quot;Occasia&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) collects,
            uses, stores, and shares personal information when you use our website, venue and organiser tools,
            and related services (collectively, the &quot;Platform&quot;). Occasia is a venue booking and event space
            platform that connects event organisers with venues for enquiries, bookings, messaging, and payments.
          </p>
        </section>

        <section className="privacy-section">
          <h2>1. Information we collect</h2>
          <p><strong>Information you provide</strong></p>
          <ul>
            <li>Account details: name, email address, phone number, and password (if you register with email).</li>
            <li>Profile and preferences: user type (organiser or venue), organisation name, and communication preferences.</li>
            <li>Event and booking data: event type, dates, guest counts, location preferences, budgets, messages, and booking history.</li>
            <li>Venue listing data: venue name, address, photos, capacity, pricing, availability, and owner contact details.</li>
            <li>
              Payment-related information: billing contact details and transaction references. Card numbers are
              processed by Stripe; we do not store full card numbers on our servers.
            </li>
            <li>Support and correspondence: content of emails, chat messages, or forms you send to us.</li>
          </ul>
          <p><strong>Information from Google Sign-In</strong></p>
          <p>
            If you choose to sign in with Google, we receive information from your Google account according to
            the permissions you grant. We request access to your basic profile information, including your name,
            email address, and profile picture (via the <code>openid</code>, <code>email</code>, and{' '}
            <code>profile</code> scopes). We use this information only to create and secure your Occasia account,
            identify you across sessions, and communicate with you about your bookings. We do not use Google user
            data for advertising, and we do not sell Google user data to third parties.
          </p>
          <p><strong>Information collected automatically</strong></p>
          <ul>
            <li>Device and log data: IP address, browser type, operating system, referring URLs, pages viewed, and timestamps.</li>
            <li>Cookies and similar technologies: see Section 4 below.</li>
            <li>Analytics: we use Google Analytics to understand how the Platform is used (aggregated usage statistics).</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>2. How we use your information</h2>
          <p>We use personal information to:</p>
          <ul>
            <li>Provide the Platform, including venue discovery, brief submission, booking requests, messaging, and payments.</li>
            <li>Authenticate you and maintain your account (including Google Sign-In).</li>
            <li>Match organisers with suitable venues and facilitate communication between parties.</li>
            <li>Process payments and payouts through our payment partners (such as Stripe).</li>
            <li>Send service-related emails and notifications (booking updates, account verification, security alerts).</li>
            <li>Improve, secure, and troubleshoot the Platform and prevent fraud or abuse.</li>
            <li>Comply with legal obligations and respond to lawful requests.</li>
            <li>Send marketing communications where permitted by law and where you have opted in; you may opt out at any time.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. Legal bases (UK &amp; EEA users)</h2>
          <p>Where applicable, we process personal data on the following bases:</p>
          <ul>
            <li>Performance of a contract (providing the Platform and bookings you request).</li>
            <li>Legitimate interests (securing the Platform, improving services, and communicating with you about your account).</li>
            <li>Consent (where required for optional marketing or non-essential cookies).</li>
            <li>Legal obligation (tax, accounting, or regulatory requirements).</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>4. Cookies and analytics</h2>
          <p>
            We use cookies and similar technologies to keep you signed in, remember preferences, and measure site
            performance. You can control cookies through your browser settings. Disabling cookies may limit some
            features (such as staying logged in). We use Google Analytics (Google LLC) to collect aggregated usage
            data; Google&apos;s processing is governed by{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Google&apos;s Privacy Policy
            </a>
            .
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. How we share information</h2>
          <p>We do not sell your personal information. We may share information with:</p>
          <ul>
            <li>Venues and organisers, as needed to fulfil a booking or enquiry you initiate.</li>
            <li>Service providers who help us operate the Platform (hosting, email delivery, analytics, customer support, and payment processing).</li>
            <li>Stripe and other payment processors, to process transactions you authorise.</li>
            <li>Google, when you use Google Sign-In or when we use Google Maps or Analytics on the Platform, subject to Google&apos;s terms and policies.</li>
            <li>Professional advisers or authorities when required by law or to protect rights, safety, and security.</li>
            <li>A successor entity in connection with a merger, acquisition, or sale of assets, with continued protection of your data as described in this policy.</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>6. Google API Services and Limited Use</h2>
          <p>
            Occasia&apos;s use of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. Google user data is used only to provide or improve
            user-facing features of the Platform (account sign-in and profile display), not for serving ads, and
            not for sale to data brokers. Human access to Google user data is limited to security, support, and
            legal compliance needs.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. Data retention</h2>
          <p>
            We retain personal information for as long as your account is active or as needed to provide the
            Platform, resolve disputes, enforce agreements, and meet legal retention requirements. Booking and
            payment records may be kept for longer periods where required for accounting or tax law. You may
            request deletion as described in Section 9.
          </p>
        </section>

        <section className="privacy-section">
          <h2>8. Security</h2>
          <p>
            We implement technical and organisational measures designed to protect personal information, including
            encryption in transit (HTTPS), access controls, and secure handling of authentication tokens. No method
            of transmission or storage is completely secure; please use a strong password and keep your credentials
            confidential.
          </p>
        </section>

        <section className="privacy-section">
          <h2>9. Your rights and choices</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access, correct, or update your personal information (via your account settings or by contacting us).</li>
            <li>Request deletion of your account and associated data, subject to legal exceptions.</li>
            <li>Object to or restrict certain processing, or withdraw consent where processing is consent-based.</li>
            <li>Opt out of marketing emails using the unsubscribe link in those messages.</li>
            <li>Lodge a complaint with your local data protection authority.</li>
          </ul>
          <p>
            To exercise these rights, contact us at{' '}
            <a href="mailto:hello@occasia.io">hello@occasia.io</a>. If you signed in with Google, you can also
            revoke Occasia&apos;s access from your{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
              Google Account permissions
            </a>{' '}
            page.
          </p>
        </section>

        <section className="privacy-section">
          <h2>10. International transfers</h2>
          <p>
            We may process and store information in the United Kingdom and other countries where our service
            providers operate. Where required, we use appropriate safeguards (such as standard contractual clauses)
            for transfers outside the UK or EEA.
          </p>
        </section>

        <section className="privacy-section">
          <h2>11. Children</h2>
          <p>
            The Platform is not directed at children under 16, and we do not knowingly collect personal information
            from children. If you believe a child has provided us data, contact us and we will delete it.
          </p>
        </section>

        <section className="privacy-section">
          <h2>12. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the revised policy on this page and
            update the &quot;Last updated&quot; date. Material changes may be communicated by email or a notice on the
            Platform.
          </p>
        </section>

        <section className="privacy-section">
          <h2>13. Contact us</h2>
          <p>
            For privacy questions or requests, contact:
          </p>
          <p>
            <strong>Occasia</strong>
            <br />
            Email: <a href="mailto:hello@occasia.io">hello@occasia.io</a>
            <br />
            Website: <a href="https://www.occasia.io/">https://www.occasia.io</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
